import { AircraftType } from '@pelicargo/types';
import { prettyDate } from '@pelicargo/utils';
import { formatDuration, intervalToDuration, parseISO } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { useMemo } from 'react';

import { API } from '../utils/apiTypes';

const calculateLayoverDuration = (departsAt: string, arrivesAt: string) => {
  if (!departsAt || !arrivesAt) return '';
  const departureDate = parseISO(departsAt);
  const arrivalDate = parseISO(arrivesAt);
  const duration = intervalToDuration({
    start: departureDate,
    end: arrivalDate,
  });
  return formatDuration(duration);
};

// Convert an ISO string to a Date object in the specified timezone
const parseDate = (dateString: string, timeZone: string): Date => {
  const date = fromZonedTime(dateString, timeZone);
  return toZonedTime(date, timeZone);
};

type Departure = {
  time: string;
  timezone: string;
};

type Arrival = {
  time: string;
  timezone: string;
};

const calculateFlightTime = (departure: Departure, arrival: Arrival) => {
  if (!arrival?.time || !departure?.time) return null;

  const departureTime = parseDate(departure.time, departure?.timezone);
  const arrivalTime = parseDate(arrival.time, arrival?.timezone);

  const duration = intervalToDuration({
    start: departureTime,
    end: arrivalTime,
  });

  return duration;
};

export type IFlightStop = {
  variant: 'stop';
  type: 'depart' | 'arrive';
  date: string;
  time: string;
  airportName: string;
  airportId: string;

  showDateTime: boolean;
};

export type IFlightLeg = {
  variant: 'leg';
  flightDuration: string;
  airlineLogo: string;
  providerId: string;
  flightNumber: string;
  aircraftType: string;
  aircraftName: string;
};

export type IFlightLayover = {
  variant: 'layover';
  layoverDuration: string | null;
};

export type FlightData = IFlightStop | IFlightLeg | IFlightLayover;

/* 
TEMP: 
Synthesizes a fake first "stop" + RFS leg so EXW pickup zip renders in the flight timeline. 
This patches around flight_path not modeling inland pickup today.
*/

// TODO: Rethink flight_path to include pickup origin as a first-class segment.
export const buildPickupFlightDataPrefix = (pickupOrigin: string): FlightData[] => [
  {
    variant: 'stop',
    type: 'depart',
    showDateTime: false,
    time: '',
    date: '',
    airportName: '',
    airportId: pickupOrigin,
  },
  {
    variant: 'leg',
    flightDuration: '',
    airlineLogo: '',
    providerId: '',
    flightNumber: '',
    aircraftType: AircraftType.RFS,
    aircraftName: '',
  },
];

type UseFlightDataOptions = {
  pickupOrigin?: string;
};

export const useFlightData = (flightPath: API.FinalQuote['flight_path'], options?: UseFlightDataOptions) => {
  const pickupOrigin = options?.pickupOrigin;

  const flightData = useMemo(() => {
    const sequentialData: FlightData[] = [];

    if (!flightPath) return pickupOrigin ? buildPickupFlightDataPrefix(pickupOrigin) : sequentialData;

    const showDateTime = flightPath.some((leg) => leg?.departs_at || leg?.arrives_at);

    flightPath?.forEach((leg, index) => {
      const nextLeg = flightPath?.[index + 1];

      const departs_at = leg?.departs_at;
      const arrives_at = leg?.arrives_at;
      const originTimeZone = leg?.origin_airport?.time_zone;
      const destinationTimeZone = leg?.destination_airport?.time_zone;

      // Add departure stop
      sequentialData.push({
        variant: 'stop',
        type: 'depart',
        showDateTime,
        time: prettyDate(departs_at, { timeZone: originTimeZone, onlyTime: true }),
        date: prettyDate(departs_at, { timeZone: originTimeZone, hideYear: true }),
        airportName: leg?.origin_airport?.name,
        airportId: leg?.origin_airport?.id,
      });

      // Add flight leg
      const flightDuration = calculateFlightTime(
        { time: departs_at as any, timezone: originTimeZone },
        { time: arrives_at as any, timezone: destinationTimeZone },
      );
      const prettyFlightDuration = flightDuration ? formatDuration(flightDuration) : '';
      sequentialData.push({
        variant: 'leg',
        flightDuration: prettyFlightDuration,
        airlineLogo: leg?.provider?.logo_uri ?? '',
        providerId: leg?.provider_id,
        flightNumber: leg?.flight_number,
        aircraftType: leg?.aircraft_type,
        aircraftName: leg?.aircraft?.name ?? '',
      });

      // Add arrival stop
      sequentialData.push({
        variant: 'stop',
        type: 'arrive',
        showDateTime,
        time: prettyDate(arrives_at, { timeZone: destinationTimeZone, onlyTime: true }),
        date: prettyDate(arrives_at, { timeZone: destinationTimeZone, hideYear: true }),
        airportName: leg?.destination_airport?.name,
        airportId: leg?.destination_airport?.id,
      });

      // Add layover if there is a next leg
      if (nextLeg) {
        sequentialData.push({
          variant: 'layover',
          layoverDuration: calculateLayoverDuration(arrives_at as any, nextLeg?.departs_at as any),
        });
      }
    });

    if (pickupOrigin) return [...buildPickupFlightDataPrefix(pickupOrigin), ...sequentialData];

    return sequentialData;
  }, [flightPath, pickupOrigin]);

  const timeInTransit = useMemo(() => {
    const totalDuration = flightPath?.reduce(
      (acc, leg, index) => {
        const flightDuration = calculateFlightTime(
          {
            time: leg?.departs_at as any,
            timezone: leg?.origin_airport?.time_zone,
          },
          {
            time: leg?.arrives_at as any,
            timezone: leg?.destination_airport?.time_zone,
          },
        );
        const nextLeg = flightPath?.[index + 1];
        const layoverDuration = calculateFlightTime(
          {
            time: leg?.arrives_at as any,
            timezone: leg?.origin_airport?.time_zone,
          },
          {
            time: nextLeg?.departs_at as any,
            timezone: nextLeg?.destination_airport?.time_zone,
          },
        );

        const flightMinutes = flightDuration?.minutes || 0;
        const flightHours = flightDuration?.hours || 0;

        const layoverHours = layoverDuration?.hours || 0;
        const layoverMinutes = layoverDuration?.minutes || 0;

        const minutes = acc.minutes + flightMinutes + layoverMinutes;
        const hours = acc.hours + flightHours + layoverHours;

        return {
          hours: hours + Math.floor(minutes / 60),
          minutes: minutes % 60,
        };
      },
      { hours: 0, minutes: 0 },
    );

    const formattedDuration = formatDuration(
      { hours: totalDuration?.hours, minutes: totalDuration?.minutes },
      { format: ['hours', 'minutes'] },
    );

    return formattedDuration.replace(' minutes', 'm').replace(' hours', 'h');
  }, [flightPath]);

  const shouldShowFlightData = useMemo(() => {
    return flightData?.every((data) => {
      if (data?.variant !== 'stop') return true;
      return data?.airportName && data?.airportId;
    });
  }, [flightData]);

  return {
    flightData,
    timeInTransit,
    shouldShowFlightData,
  };
};
